import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Stack, Divider, IconButton, Button, Grid,
  Card, CardContent, Tabs, Tab, Tooltip, CircularProgress, useTheme, useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalendarToday as CalendarIcon, Edit as EditIcon, ArrowBack as BackIcon,
  Translate as TranslateIcon, LocalOffer as TagIcon, SentimentSatisfied as MoodIcon,
  Assessment as AnalyticsIcon, MenuBook as StoryIcon, CheckCircle as CheckIcon,
  VolumeUp as ListenIcon, Pause as PauseIcon, Speed as FluencyIcon
} from '@mui/icons-material';
import JournalCorrectionsReview from './JournalCorrectionsReview';
import AddToVocabButton from './AddToVocabButton';
import apiClient from '../services/apiClient';
import { moodLabel } from './journalMoods';
import { diffWords, findReasons } from '../utils/wordDiff';

// 0-sample silent WAV: played inside the tap so iOS allows the real audio to
// start after the async request returns.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

// One highlighted correction in the "What changed" view, with its reason(s).
function ChangeSpan({ part, segments }) {
  const reasons = findReasons(segments, part.removed, part.added);
  const addedCore = part.added.replace(/\s+$/, '');
  const addedTail = part.added.slice(addedCore.length);
  const title = reasons.length
    ? (
      <Box>
        {reasons.map((r, i) => (
          <Typography key={i} variant="body2" sx={{ mt: i ? 0.75 : 0 }}>
            {r.explanation || `${r.text} → ${r.correction}`}
          </Typography>
        ))}
      </Box>
    )
    : 'Corrected';
  return (
    <>
      <Tooltip title={title} arrow enterTouchDelay={0} leaveTouchDelay={5000}>
        <Box component="span" sx={{ cursor: 'help' }}>
          {part.removed && (
            <Box component="span" sx={{
              textDecoration: 'line-through', color: 'error.main', borderRadius: 0.5, px: 0.25,
              bgcolor: (t) => alpha(t.palette.error.main, 0.08)
            }}>
              {part.removed.trimEnd()}
            </Box>
          )}
          {part.removed && addedCore && ' '}
          {addedCore && (
            <Box component="span" sx={{
              color: 'success.dark', fontWeight: 600, borderRadius: 0.5, px: 0.25,
              bgcolor: (t) => alpha(t.palette.success.main, 0.14)
            }}>
              {addedCore}
            </Box>
          )}
        </Box>
      </Tooltip>
      {addedTail || (part.removed && !addedCore ? ' ' : '')}
    </>
  );
}

const JournalEntryView = ({ entry, onBack, onEdit }) => {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- Listen to the corrected version ---
  const audioRef = useRef(null);
  const [audioState, setAudioState] = useState('idle'); // idle | loading | playing | paused | error
  useEffect(() => {
    // New entry or edited text -> drop old audio.
    setAudioState('idle');
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [entry.entry_id, entry.polished_content]);

  const handleListen = async () => {
    const existing = audioRef.current;
    if (existing && existing.src && !existing.src.startsWith('data:') && audioState !== 'loading') {
      if (!existing.paused) { existing.pause(); return; }
      if (existing.ended) existing.currentTime = 0;
      const p = existing.play();
      if (p && p.catch) p.catch(() => setAudioState('error'));
      return;
    }
    // Unlock playback inside this tap (iOS), then fetch and play the real audio.
    const audio = new Audio();
    audioRef.current = audio;
    audio.muted = true;
    audio.src = SILENT_WAV;
    const unlock = audio.play();
    if (unlock && unlock.catch) unlock.catch(() => {});
    setAudioState('loading');
    try {
      const response = await apiClient.journal.getEntryAudio(entry.entry_id);
      if (audioRef.current !== audio) return; // entry changed while loading
      audio.onplay = () => setAudioState('playing');
      audio.onpause = () => setAudioState('paused');
      audio.onended = () => setAudioState('idle');
      audio.onerror = () => setAudioState('error');
      audio.muted = false;
      audio.src = apiClient.audio.getAudioUrl(response.data.job_id);
      const p = audio.play();
      if (p && p.catch) p.catch(() => setAudioState('error'));
    } catch (e) {
      console.error('Journal audio failed', e);
      if (audioRef.current === audio) { audioRef.current = null; setAudioState('error'); }
    }
  };

  // --- What changed (word-level diff of the draft vs the corrected text) ---
  const segments = entry.analysis_data?.segments;
  const hasChanges = !!entry.polished_content && !!entry.raw_content &&
    entry.raw_content !== entry.polished_content;
  const diff = useMemo(
    () => (hasChanges ? diffWords(entry.raw_content, entry.polished_content) : null),
    [hasChanges, entry.raw_content, entry.polished_content]
  );
  const [showOriginal, setShowOriginal] = useState(false);

  const hasCorrections = entry.analysis_data?.segments?.some(s => s.correction);
  const correctionsCount = entry.analysis_data?.segments?.filter(s => s.correction).length || 0;

  return (
    <Box sx={{ pb: isMobile ? 10 : 4, minHeight: '80vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          Journal Entry
        </Typography>
        <Button
          startIcon={<EditIcon />}
          variant="outlined"
          size="small"
          onClick={() => onEdit(entry)}
        >
          {isMobile ? 'Edit' : 'Edit Entry'}
        </Button>
      </Box>

      {/* Main content card */}
      <Card>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Title */}
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {entry.title || 'Untitled Entry'}
          </Typography>

          {/* Meta info */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            <Chip
              icon={<CalendarIcon sx={{ fontSize: '1rem !important' }} />}
              label={new Date(entry.created_at).toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
              size="small"
              variant="outlined"
            />
            {entry.mood && (
              <Chip
                icon={<MoodIcon sx={{ fontSize: '1rem !important' }} />}
                label={moodLabel(entry.mood)}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {entry.fluency_score != null && (
              <Chip
                icon={<FluencyIcon sx={{ fontSize: '1rem !important' }} />}
                label={`Fluency ${entry.fluency_score}/100`}
                size="small"
                variant="outlined"
              />
            )}
            {entry.tags?.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                icon={<TagIcon sx={{ fontSize: '1rem !important' }} />}
              />
            ))}
          </Stack>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              aria-label="journal view tabs"
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab
                icon={<StoryIcon fontSize="small" />}
                iconPosition="start"
                label="Read"
                sx={{ minHeight: 48 }}
              />
              <Tab
                icon={<AnalyticsIcon fontSize="small" />}
                iconPosition="start"
                label={isMobile ? `Review${hasCorrections ? ` (${correctionsCount})` : ''}` : `Corrections & Analysis${hasCorrections ? ` (${correctionsCount})` : ''}`}
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          {/* Tab content */}
          {tab === 0 && (
            <Box>
              {/* Listen to the corrected version */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleListen}
                  disabled={audioState === 'loading'}
                  startIcon={audioState === 'loading'
                    ? <CircularProgress size={16} />
                    : audioState === 'playing' ? <PauseIcon /> : <ListenIcon />}
                  sx={{ textTransform: 'none', borderRadius: 4 }}
                >
                  {audioState === 'loading' ? 'Preparing audio…'
                    : audioState === 'playing' ? 'Pause'
                    : audioState === 'paused' ? 'Resume'
                    : 'Listen'}
                </Button>
                {audioState === 'error' && (
                  <Typography variant="caption" color="error">
                    Couldn't load the audio. Tap Listen to try again.
                  </Typography>
                )}
              </Box>

              {/* Main entry content */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? '1rem' : '1.15rem',
                  lineHeight: 1.9,
                  whiteSpace: 'pre-wrap',
                  fontFamily: '"Georgia", "Times New Roman", serif',
                  color: 'text.primary',
                  mb: 4
                }}
              >
                {entry.polished_content || entry.raw_content}
              </Typography>

              {/* Vocabulary section */}
              {entry.glossary && entry.glossary.length > 0 && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <TranslateIcon color="secondary" />
                      Vocabulary Learned ({entry.glossary.length})
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {entry.glossary.map((word, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: 'grey.50',
                              height: '100%'
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              {word.word || word.original}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {word.translation}
                            </Typography>
                            {word.context && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
                              >
                                "{word.context}"
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                              <AddToVocabButton
                                word={word.word || word.original}
                                translation={word.translation}
                                context={word.context}
                                languageCode={entry.target_language}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </>
              )}

              {/* What changed: inline highlight of the draft vs the corrected text */}
              {hasChanges && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CheckIcon color="success" />
                        What Changed
                      </Typography>
                      {diff && (
                        <Button size="small" onClick={() => setShowOriginal((v) => !v)} sx={{ textTransform: 'none' }}>
                          {showOriginal ? 'Hide original draft' : 'Show original draft'}
                        </Button>
                      )}
                    </Box>
                    {diff && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Crossed-out words are from your draft; highlighted words are the corrections.
                          {isMobile ? ' Tap' : ' Hover over'} a change to see why.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: showOriginal ? 2 : 0 }}>
                          <Typography component="div" variant="body1" sx={{ lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                            {diff.map((part, i) => (part.type === 'equal'
                              ? <span key={i}>{part.text}</span>
                              : <ChangeSpan key={i} part={part} segments={segments} />))}
                          </Typography>
                        </Paper>
                      </>
                    )}
                    {(showOriginal || !diff) && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        borderLeft: '3px solid',
                        borderColor: 'grey.400'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          color: 'text.secondary',
                          lineHeight: 1.7
                        }}
                      >
                        {entry.raw_content}
                      </Typography>
                    </Paper>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}

          {tab === 1 && (
            <JournalCorrectionsReview analysis={entry.analysis_data} targetLanguage={entry.target_language} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default JournalEntryView;